"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { PhysicsPlot } from "@/components/tools/physics-waves/plot";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  axisPrefix,
  type NumberFormatter,
  shiftExp,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  OHM_PAIRS,
  OHM_VARIABLES,
  type OhmFormula,
  type OhmValues,
  type OhmVariable,
  solveOhm,
  suggestedWattage,
} from "@/lib/tool-kit/physics-waves/ohms-law";
import {
  findUnit,
  prefixUnits,
  toBase,
  type UnitDef,
} from "@/lib/tool-kit/physics-waves/units";

/** English strings. A translated content file overrides these through `ui`. */
export const OHMS_LAW_CALCULATOR_STRINGS = {
  pairLabel: "Which two quantities do you know?",
  pairs: {
    VI: "Voltage and current",
    VR: "Voltage and resistance",
    IR: "Current and resistance",
    VP: "Voltage and power",
    IP: "Current and power",
    RP: "Resistance and power",
  },
  names: {
    V: "Voltage (V)",
    I: "Current (I)",
    R: "Resistance (R)",
    P: "Power (P)",
  },
  unitFor: "Unit for {name}",
  sigFigs: "Round to this many significant figures",
  tryLabel: "Try an example",
  presets: {
    resistor: "12 V across 470 Ω",
    usb: "5 V charger at 2 A",
    kettle: "2.4 kW kettle on 230 V",
    led: "LED resistor: 3 V at 10 mA",
  },
  results: "Results",
  enterBoth: "Enter a number in both boxes to see the other two quantities.",
  needPositive:
    "These values cannot go together. Resistance and any quantity you divide by must be above zero, and Ohm's law as used here covers positive DC values.",
  known: "You entered",
  solved: "Calculated",
  working: "The working",
  graphHeading: "Current against voltage",
  graphLabel: "I–V graph for a {resistance} resistor",
  graphDescription:
    "A straight line through the origin whose slope is 1 divided by the resistance, with a dot at {voltage} and {current}.",
  graphX: "Voltage ({unit})",
  graphY: "Current ({unit})",
  graphNote:
    "An ohmic resistor gives a straight line through the origin; its slope is 1/R, so a steeper line means a smaller resistance.",
  wheelHeading: "All twelve power-wheel formulas",
  wheelNote:
    "Every row for one quantity gives the same answer — a quick check on your own working.",
  columnQuantity: "Quantity",
  columnFormula: "Formula",
  columnValue: "Value",
  ratingTip:
    "If this power is dissipated in a resistor, choose a rating of at least {rating} (twice the {power} dissipated) so it runs cool.",
  ratingTooHigh:
    "{power} is more than a standard resistor handles: use a wirewound or power resistor rated well above it.",
  copy: "Copy results",
  copied: "Copied!",
  copyText: "V = {V}, I = {I}, R = {R}, P = {P}",
} as const;

type Strings = typeof OHMS_LAW_CALCULATOR_STRINGS;
type PairKey = keyof Strings["pairs"];

const SYMBOLS: Record<OhmVariable, string> = { V: "V", I: "A", R: "Ω", P: "W" };

const UNITS: Record<OhmVariable, UnitDef[]> = {
  V: prefixUnits("V", ["µ", "m", "", "k", "M"]),
  I: prefixUnits("A", ["µ", "m", "", "k"]),
  R: prefixUnits("Ω", ["m", "", "k", "M", "G"]),
  P: prefixUnits("W", ["µ", "m", "", "k", "M"]),
};

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const RATING_FORMAT = { maximumFractionDigits: 3, useGrouping: false } as const;
const WHEEL_TERM = /([VIRP])(²)?/g;

interface Entry {
  value: string;
  unit: string;
}

type Entries = Record<OhmVariable, Entry>;

const INITIAL: Entries = {
  V: { value: "12", unit: "V" },
  I: { value: "25", unit: "mA" },
  R: { value: "470", unit: "Ω" },
  P: { value: "300", unit: "mW" },
};

const PRESET_DATA: Record<
  keyof Strings["presets"],
  { pair: PairKey; entries: Partial<Entries> }
> = {
  resistor: {
    pair: "VR",
    entries: { V: { value: "12", unit: "V" }, R: { value: "470", unit: "Ω" } },
  },
  usb: {
    pair: "VI",
    entries: { V: { value: "5", unit: "V" }, I: { value: "2", unit: "A" } },
  },
  kettle: {
    pair: "VP",
    entries: {
      V: { value: "230", unit: "V" },
      P: { value: "2.4", unit: "kW" },
    },
  },
  led: {
    pair: "VI",
    entries: { V: { value: "3", unit: "V" }, I: { value: "10", unit: "mA" } },
  },
};

function pairVariables(key: PairKey): [OhmVariable, OhmVariable] {
  return [key[0] as OhmVariable, key[1] as OhmVariable];
}

function show(
  fmt: NumberFormatter,
  variable: OhmVariable,
  value: number,
  sig: number
): string {
  return withSiPrefix(fmt, value, SYMBOLS[variable], sig);
}

/** "I = V ÷ R" with the numbers put in: "12 V ÷ 470 Ω". */
function substituted(
  formula: OhmFormula,
  values: OhmValues,
  fmt: NumberFormatter,
  sig: number
): string {
  const right = formula.text.split(" = ")[1] ?? "";
  const word = (letter: string) =>
    show(fmt, letter as OhmVariable, values[letter as OhmVariable], sig);
  return right.replace(WHEEL_TERM, (_match, letter: string, square?: string) =>
    square ? `(${word(letter)})²` : word(letter)
  );
}

function workingLines(
  pair: [OhmVariable, OhmVariable],
  formulas: Record<OhmVariable, OhmFormula[]>,
  values: OhmValues,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const lines: string[] = [];
  for (const variable of OHM_VARIABLES) {
    if (pair.includes(variable)) {
      continue;
    }
    const formula = formulas[variable].find(
      (f) => pair.includes(f.uses[0]) && pair.includes(f.uses[1])
    );
    if (formula) {
      lines.push(
        `${formula.text} = ${substituted(formula, values, fmt, sig)} = ${show(fmt, variable, values[variable], sig)}`
      );
    }
  }
  return lines;
}

function Graph({
  values,
  strings,
  fmt,
  sig,
}: {
  values: OhmValues;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const xPrefix = axisPrefix(values.V * 1.25);
  const yPrefix = axisPrefix(values.I * 1.25);
  const toX = (v: number) => shiftExp(v, -xPrefix.exp);
  const toY = (v: number) => shiftExp(v, -yPrefix.exp);
  const vMax = values.V * 1.25;
  const label = show(fmt, "R", values.R, sig);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
      <PhysicsPlot
        curves={[
          {
            points: [
              [0, 0],
              [toX(vMax), toY(vMax / values.R)],
            ],
          },
        ]}
        description={t(strings.graphDescription, {
          voltage: show(fmt, "V", values.V, sig),
          current: show(fmt, "I", values.I, sig),
        })}
        dots={[
          {
            x: toX(values.V),
            y: toY(values.I),
            label: `${show(fmt, "V", values.V, sig)}, ${show(fmt, "I", values.I, sig)}`,
          },
        ]}
        formatX={(v) => fmt(v, { maximumFractionDigits: 6 })}
        formatY={(v) => fmt(v, { maximumFractionDigits: 6 })}
        id="ohm-graph"
        label={t(strings.graphLabel, { resistance: label })}
        xRange={[0, toX(vMax)]}
        xTitle={t(strings.graphX, { unit: `${xPrefix.symbol}V` })}
        yRange={[0, toY(vMax / values.R)]}
        yTitle={t(strings.graphY, { unit: `${yPrefix.symbol}A` })}
      />
      <p className="text-muted-foreground text-xs">{strings.graphNote}</p>
    </div>
  );
}

function Wheel({
  formulas,
  strings,
  fmt,
  sig,
}: {
  formulas: Record<OhmVariable, OhmFormula[]>;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const rows = OHM_VARIABLES.flatMap((variable) =>
    formulas[variable].map((f) => [
      strings.names[variable],
      f.text,
      show(fmt, variable, f.value, sig),
    ])
  );
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.wheelHeading}</h3>
      <DataTable
        head={[
          strings.columnQuantity,
          strings.columnFormula,
          strings.columnValue,
        ]}
        rows={rows}
      />
      <p className="text-muted-foreground text-xs">{strings.wheelNote}</p>
    </div>
  );
}

function RatingTip({
  power,
  strings,
  fmt,
  sig,
}: {
  power: number;
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const rating = suggestedWattage(power);
  const shownPower = show(fmt, "P", power, sig);
  if (rating === null) {
    return <Notice>{t(strings.ratingTooHigh, { power: shownPower })}</Notice>;
  }
  return (
    <Notice>
      {t(strings.ratingTip, {
        rating: `${fmt(rating, RATING_FORMAT)} W`,
        power: shownPower,
      })}
    </Notice>
  );
}

export default function OhmsLawCalculatorWidget() {
  const strings = useToolUi(OHMS_LAW_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [pairKey, setPairKey] = useState<PairKey>("VR");
  const [entries, setEntries] = useState<Entries>(INITIAL);
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const sig = Number(sigText);
  const pair = pairVariables(pairKey);

  const known: Partial<Record<OhmVariable, number>> = {};
  for (const variable of pair) {
    const parsed = parseNumberInput(entries[variable].value);
    if (parsed !== null) {
      known[variable] = toBase(
        parsed,
        findUnit(UNITS[variable], entries[variable].unit)
      );
    }
  }
  const outcome = solveOhm(pair[0], pair[1], known);

  const setEntry = (variable: OhmVariable, patch: Partial<Entry>) =>
    setEntries((current) => ({
      ...current,
      [variable]: { ...current[variable], ...patch },
    }));

  const applyPreset = (key: keyof Strings["presets"]) => {
    const preset = PRESET_DATA[key];
    setPairKey(preset.pair);
    setEntries((current) => ({ ...current, ...preset.entries }));
  };

  return (
    <>
      <PillGroup
        label={strings.pairLabel}
        name="ohm-pair"
        onChange={setPairKey}
        options={OHM_PAIRS.map(([a, b]) => ({
          value: `${a}${b}` as PairKey,
          label: strings.pairs[`${a}${b}` as PairKey],
        }))}
        value={pairKey}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {pair.map((variable) => (
          <QuantityField
            id={`ohm-${variable}`}
            key={variable}
            label={strings.names[variable]}
            onChange={(value) => setEntry(variable, { value })}
            onUnitChange={(unit) => setEntry(variable, { unit })}
            unit={entries[variable].unit}
            unitLabel={t(strings.unitFor, { name: strings.names[variable] })}
            units={UNITS[variable]}
            value={entries[variable].value}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {(Object.keys(PRESET_DATA) as (keyof Strings["presets"])[]).map(
          (key) => (
            <button
              className={BUTTON_SMALL}
              key={key}
              onClick={() => applyPreset(key)}
              type="button"
            >
              {strings.presets[key]}
            </button>
          )
        )}
      </div>
      <div className="max-w-xs">
        <SelectField
          id="ohm-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <>
            <StatGrid>
              {OHM_VARIABLES.map((variable) => (
                <StatCard
                  emphasis={!pair.includes(variable)}
                  key={variable}
                  label={`${strings.names[variable]} — ${pair.includes(variable) ? strings.known : strings.solved}`}
                  value={show(fmt, variable, outcome.values[variable], sig)}
                />
              ))}
            </StatGrid>
            <WorkingSteps
              heading={strings.working}
              lines={workingLines(
                pair,
                outcome.formulas,
                outcome.values,
                fmt,
                sig
              )}
            />
            <RatingTip
              fmt={fmt}
              power={outcome.values.P}
              sig={sig}
              strings={strings}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copy}
              text={t(strings.copyText, {
                V: show(fmt, "V", outcome.values.V, sig),
                I: show(fmt, "I", outcome.values.I, sig),
                R: show(fmt, "R", outcome.values.R, sig),
                P: show(fmt, "P", outcome.values.P, sig),
              })}
            />
            {outcome.values.V > 0 &&
            outcome.values.I > 0 &&
            outcome.values.R > 0 ? (
              <>
                <Graph
                  fmt={fmt}
                  sig={sig}
                  strings={strings}
                  values={outcome.values}
                />
                <Wheel
                  fmt={fmt}
                  formulas={outcome.formulas}
                  sig={sig}
                  strings={strings}
                />
              </>
            ) : null}
          </>
        ) : null}
        {!outcome.ok && outcome.error === "need-two" ? (
          <Notice>{strings.enterBoth}</Notice>
        ) : null}
        {!outcome.ok && outcome.error === "need-positive" ? (
          <ErrorNotice>{strings.needPositive}</ErrorNotice>
        ) : null}
      </section>
    </>
  );
}
